import type { AxiosError } from 'axios'

const REDACTED = '[redacted]'

const sensitiveHeaders = ['authorization', 'cookie', 'x-verified-token']

/**
 * What an error report may say about a failed request. The raw AxiosError carries the request's
 * config, and a document link's url holds a signed `?token=` while every request's headers hold
 * the session's `x-verified-token` — neither belongs in a third party's error log.
 */
export default function redactRequestError(error: AxiosError): {
  code: string | undefined
  headers: Record<string, unknown>
  message: string
  method: string | undefined
  responseData: unknown
  status: number | undefined
  url: string | undefined
} {
  return {
    code: error.code,
    headers: redactHeaders(error.config?.headers),
    message: error.message,
    method: error.config?.method,
    responseData: error.response?.data,
    status: error.response?.status,
    url: redactUrl(error.config?.url),
  }
}

export function redactUrl(url: string | undefined): string | undefined {
  if (url === undefined) {
    return undefined
  }

  return url.replace(/([?&]token=)[^&#]*/gi, `$1${REDACTED}`)
}

export function redactParams(params: unknown): unknown {
  if (params === null || typeof params !== 'object') {
    return params
  }

  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).map(([name, value]) => [
      name,
      name.toLowerCase() === 'token' ? REDACTED : value,
    ]),
  )
}

function redactHeaders(headers: unknown): Record<string, unknown> {
  if (headers === null || typeof headers !== 'object') {
    return {}
  }

  const plain =
    'toJSON' in headers && typeof headers.toJSON === 'function'
      ? (headers.toJSON() as Record<string, unknown>)
      : { ...(headers as Record<string, unknown>) }

  return Object.fromEntries(
    Object.entries(plain).map(([name, value]) => [
      name,
      sensitiveHeaders.includes(name.toLowerCase()) ? REDACTED : value,
    ]),
  )
}
